import type { RacialId, RuneId, SlotKey, SpellId } from '../types';

/** Client → server message names (discrete events; movement uses the input channel). */
export const ClientMessage = {
  Ready: 'ready',
  Settings: 'settings',
  Start: 'start',
  Cast: 'cast',
  /** Fight only. Payload: `RacialMsg` (the Space racial toward the cursor: guard or `active`). */
  Racial: 'racial',
  Character: 'character',
  /** Payload: `ShopMsg`. */
  Shop: 'shop',
  /** Host only, lobby only. Payload: `AddBotMsg`. */
  AddBot: 'addBot',
  /** Host only, lobby only. Payload: `RemoveBotMsg`. */
  RemoveBot: 'removeBot',
  /** Host only, lobby only. Payload: `BotTierMsg`. */
  BotTier: 'botTier',
} as const;
export type ClientMessage = (typeof ClientMessage)[keyof typeof ClientMessage];

/** Server → client broadcasts. Transient effects only — authoritative results live in state. */
export const ServerMessage = {
  Cast: 'cast',
  /** A validated active racial (Stone Guard or an `active` kind), for the clip, VFX and audio. */
  Racial: 'racial',
  /** A Rogue's Stealth started, broke or ran out (the smoke puffs). Broadcast to everyone. */
  Stealth: 'stealth',
  /**
   * Sent to one client only: it stopped or started receiving a player's position (Stealth). On
   * `visible: false` the client drops that player's interpolation buffer.
   */
  Visibility: 'visibility',
  Hit: 'hit',
  /** A Shield sent a projectile or a Thrust back. Broadcast before the tick's hits. */
  Reflect: 'reflect',
  /** A stone destroyed a projectile or hook (O5). Broadcast after reflects, before hits. */
  Impact: 'impact',
  /** Two opposing projectiles cancelled each other. Broadcast after impacts, before hits. */
  Clash: 'clash',
  Kill: 'kill',
  /** An Overcharge bolt fired after its windup (the `Cast` was only the telegraph). */
  Bolt: 'bolt',
  /**
   * A rune blast for VFX: Detonation, Blink Strike, a Repulse nova or a Splinter burst. Damage
   * still arrives as `Hit` events.
   */
  Blast: 'blast',
  RoundResult: 'roundResult',
  /** Sent to the host only, when an `addBot` request cannot be served. */
  BotError: 'botError',
  /** A human joined a full lobby and took a bot's seat. Broadcast to the room. */
  BotMadeRoom: 'botMadeRoom',
} as const;
export type ServerMessage = (typeof ServerMessage)[keyof typeof ServerMessage];

/** A warlock a bolt struck, and where (sim units, in the rewound frame the caster saw). */
export interface BoltStrike {
  id: string;
  x: number;
  y: number;
}

/** Lightning's result: shared by `CastEvent` (instant bolts) and `BoltEvent` (Overcharge). */
export interface BoltResult {
  /**
   * Every warlock the bolt struck, nearest first: one for a plain bolt, several for Pierce.
   * Absent on a miss.
   */
  hits?: BoltStrike[];
  /** Chain: the jump from the struck warlock to a second one. Absent when it didn't jump. */
  chain?: { fromId: string; toId: string; x: number; y: number };
  /**
   * Where the bolt stopped at a stone (O5). Absent when it reached the target point or (without
   * Pierce) struck a warlock.
   */
  endX?: number;
  endY?: number;
}

/** A validated cast, broadcast so every client can play the cast and run the projectile locally. */
export interface CastEvent extends BoltResult {
  casterId: string;
  slot: SlotKey;
  spellId: SpellId;
  /** The target after `clampCastTarget`, not the raw client input. */
  x: number;
  y: number;
  /** Server clock ms. */
  at: number;
  /** The caster's active rune; absent = the base spell. */
  rune?: RuneId;
  /**
   * Overcharge: the cast is only the telegraph; the bolt fires at this server time (a `Bolt`
   * event follows unless the caster dies first).
   */
  fireAt?: number;
  /** Recall: this cast returned the caster to the blink origin (x, y). */
  recall?: true;
  /**
   * @deprecated Use `hits`. Lightning only: the first entry of `hits` (id and contact point), kept
   * until the client's audio mapping reads `hits`.
   */
  hitId?: string;
  hitX?: number;
  hitY?: number;
}

/** An Overcharge bolt fired: from (x0, y0) along the aim locked at cast, to (x, y). */
export interface BoltEvent extends BoltResult {
  casterId: string;
  spellId: SpellId;
  rune: RuneId;
  x0: number;
  y0: number;
  x: number;
  y: number;
  /** Server clock ms. */
  at: number;
}

/** A rune blast centred on (x, y) with radius `r`. VFX only; its hits are `Hit` events. */
export interface BlastEvent {
  ownerId: string;
  spellId: SpellId;
  rune: RuneId;
  x: number;
  y: number;
  r: number;
  /** Server clock ms. */
  at: number;
}

export interface HitEvent {
  targetId: string;
  sourceId: string;
  spellId: SpellId;
  damage: number;
  kbX: number;
  kbY: number;
  /** Projectile and hook hits only: the removed `ProjectileState` id. */
  projectileId?: string;
  /** Stone Guard reduced this hit (it came from inside the arc). Absent otherwise. */
  guarded?: true;
  /** The rune of the spell that hit; absent = the base spell. */
  rune?: RuneId;
  /** A burn tick (Kindling): damage only, no knockback. */
  burn?: true;
}

/**
 * A validated active racial cast. State (`guardUntil`, `guardFacing`, `stealthUntil`,
 * `racialCooldownEndsAt`) is authoritative.
 */
export interface RacialEvent {
  casterId: string;
  racialId: RacialId;
  /** Guard facing (radians); the caster's facing for the other racials. */
  facing: number;
  /** Server clock ms. */
  at: number;
  /**
   * Server clock ms when the effect ends: the guard (unless a spell cast ends it first) or
   * Stealth (unless it breaks).
   */
  until: number;
}

/** Why Stealth broke early. */
export type StealthBreakCause = 'cast' | 'hit' | 'lava' | 'collision' | 'death' | 'roundEnd';

/**
 * Stealth started, broke or ran out (`end`), at (x, y): the only positions everyone gets while a
 * Rogue is hidden. A `break` or `end` also sets the racial cooldown (`racialCooldownEndsAt`).
 */
export interface StealthEvent {
  casterId: string;
  phase: 'start' | 'break' | 'end';
  /** `break` only. */
  cause?: StealthBreakCause;
  x: number;
  y: number;
  /** Server clock ms. */
  at: number;
}

/** Sent to one client: whether it now receives `id`'s position fields. */
export interface VisibilityEvent {
  id: string;
  visible: boolean;
}

/** A Shield reflected a projectile, a hook or a Thrust. */
export interface ReflectEvent {
  /** The shield's owner. */
  holderId: string;
  /** Who cast the reflected spell. */
  sourceId: string;
  spellId: SpellId;
  /** Contact point, sim units. */
  x: number;
  y: number;
  /** Projectile and hook reflects only (absent for Thrust). The projectile keeps its id. */
  projectileId?: string;
  /** The projectile's new velocity (absent for Thrust). */
  vx?: number;
  vy?: number;
  /** A Mirror shield empowered the reflect. */
  mirror?: true;
}

/** A stone stopped a projectile or hook; it was removed from state and dealt nothing. */
export interface ImpactEvent {
  /** The removed `ProjectileState` id. */
  projectileId: string;
  spellId: SpellId;
  /** The projectile's owner (after any reflect). */
  ownerId: string;
  obstacleId: number;
  /** Contact point (the projectile's centre when it touched the stone), sim units. */
  x: number;
  y: number;
}

/** Two opposing projectiles touched and cancelled each other; both were removed from state. */
export interface ClashEvent {
  /** The removed `ProjectileState` ids. */
  aId: string;
  bId: string;
  /** Their owners (after any reflect). */
  aOwner: string;
  bOwner: string;
  /** Midpoint of the two centres at contact, sim units. */
  x: number;
  y: number;
}

/** `hit`: the last spell hit took the victim's last HP. `lava`: it burned to death. */
export type KillCause = 'hit' | 'lava';

export interface KillEvent {
  victimId: string;
  killerId?: string;
  assistId?: string;
  cause: KillCause;
  /**
   * The credited killer's last spell on the victim (the kill-feed icon). For a lava death this is
   * the spell that knocked it in; absent when nobody is credited.
   */
  spellId?: SpellId;
}

export interface RoundResultEvent {
  round: number;
  winnerIds: string[];
  gold: Record<string, number>;
}

/**
 * WebSocket close code the server uses when it takes a bot's seat away (host removal, or eviction
 * to make room for a human). The bot must not try to reconnect.
 */
export const BOT_EVICTED_CLOSE_CODE = 4100;

/** `unavailable`: the bot service is disabled or unreachable. `full`: no free seats. */
export interface BotErrorEvent {
  reason: 'unavailable' | 'full';
}

/** A bot was evicted so a human could join a full lobby (not sent when a pending ticket is dropped). */
export interface BotMadeRoomEvent {
  botName: string;
  /** The joining human's sanitized name; '' when they sent none. */
  humanName: string;
}

export interface ServerMessages {
  [ServerMessage.Cast]: CastEvent;
  [ServerMessage.Racial]: RacialEvent;
  [ServerMessage.Stealth]: StealthEvent;
  [ServerMessage.Visibility]: VisibilityEvent;
  [ServerMessage.Hit]: HitEvent;
  [ServerMessage.Reflect]: ReflectEvent;
  [ServerMessage.Impact]: ImpactEvent;
  [ServerMessage.Clash]: ClashEvent;
  [ServerMessage.Kill]: KillEvent;
  [ServerMessage.Bolt]: BoltEvent;
  [ServerMessage.Blast]: BlastEvent;
  [ServerMessage.RoundResult]: RoundResultEvent;
  [ServerMessage.BotError]: BotErrorEvent;
  [ServerMessage.BotMadeRoom]: BotMadeRoomEvent;
}

/** Room names registered by the server. */
export const RoomName = {
  Lobby: 'lobby',
  Arena: 'arena',
} as const;

/** Realtime-listing metadata published by each arena room. */
export interface ArenaRoomMetadata {
  name: string;
  hostName: string;
  mode: string;
  /** Humans + bots. */
  players: number;
  /** Bot seats among `players`. */
  bots: number;
  maxPlayers: number;
  phase: string;
}
